import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2, Plus, Trash2, Edit2, Save, X, IndianRupee,
} from "lucide-react";

const VendorExpenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterCategory, setFilterCategory] = useState("");

  // Add expense dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    category: "", amount: "", description: "", date: new Date().toISOString().slice(0, 10),
    vendor_name: "", invoice_number: "", payment_status: "Paid",
  });

  // New category
  const [newCategory, setNewCategory] = useState("");
  const [addingCategory, setAddingCategory] = useState(false);

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => { fetchExpenses(); }, [page, filterCategory]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/pharmeasy-expenses/categories`);
      setCategories(res.data.categories || []);
    } catch { /* ignore */ }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, size: 50 });
      if (filterCategory) params.append("category", filterCategory);
      const res = await axios.get(`${API}/pharmeasy-expenses/?${params}`);
      setExpenses(res.data.data || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    setAddingCategory(true);
    try {
      const res = await axios.post(`${API}/pharmeasy-expenses/categories`, { name: newCategory.trim() });
      setCategories(res.data.categories || []);
      setNewCategory("");
      toast.success("Category added");
    } catch {
      toast.error("Failed to add category");
    } finally {
      setAddingCategory(false);
    }
  };

  const openAddDialog = () => {
    setEditingId(null);
    setForm({
      category: "", amount: "", description: "", date: new Date().toISOString().slice(0, 10),
      vendor_name: "", invoice_number: "", payment_status: "Paid",
    });
    setDialogOpen(true);
  };

  const openEditDialog = (exp) => {
    setEditingId(exp._id);
    setForm({
      category: exp.category || "",
      amount: String(exp.amount || ""),
      description: exp.description || "",
      date: exp.date || "",
      vendor_name: exp.vendor_name || "",
      invoice_number: exp.invoice_number || "",
      payment_status: exp.payment_status || "Paid",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.category || !form.amount || !form.date) {
      toast.error("Category, Amount, and Date are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (editingId) {
        await axios.put(`${API}/pharmeasy-expenses/${editingId}`, payload);
        toast.success("Expense updated");
      } else {
        await axios.post(`${API}/pharmeasy-expenses/`, payload);
        toast.success("Expense added");
      }
      setDialogOpen(false);
      fetchExpenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save expense");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this expense?")) return;
    try {
      await axios.delete(`${API}/pharmeasy-expenses/${id}`);
      toast.success("Expense deleted");
      fetchExpenses();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const fmt = (v) => v != null ? `${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 0 })}` : "0";

  return (
    <div className="space-y-6" data-testid="vendor-expenses">
      {/* Header row */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Select value={filterCategory} onValueChange={(v) => { setFilterCategory(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-[200px] h-9 text-sm">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Badge variant="secondary" className="text-xs">{total} expenses</Badge>
        </div>
        <div className="flex items-center gap-3">
          <Input
            placeholder="New category..."
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            className="h-9 w-[160px] text-sm"
            onKeyDown={(e) => e.key === "Enter" && handleAddCategory()}
          />
          <Button size="sm" variant="outline" onClick={handleAddCategory} disabled={addingCategory || !newCategory.trim()}>
            {addingCategory ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          </Button>
          <Button size="sm" onClick={openAddDialog} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-expense-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Expense
          </Button>
        </div>
      </div>

      {/* Expenses Table */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : expenses.length === 0 ? (
            <div className="text-center py-12 text-slate-400">No expenses found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Vendor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Invoice #</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Amount</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((exp) => (
                    <tr key={exp._id} className="border-b last:border-0 hover:bg-slate-50/50">
                      <td className="px-4 py-3 align-middle text-slate-700 whitespace-nowrap">{exp.date}</td>
                      <td className="px-4 py-3 align-middle">
                        <Badge variant="outline" className="text-xs">{exp.category}</Badge>
                      </td>
                      <td className="px-4 py-3 align-middle text-slate-700">{exp.vendor_name || "-"}</td>
                      <td className="px-4 py-3 align-middle text-slate-500 max-w-[200px] truncate">{exp.description || "-"}</td>
                      <td className="px-4 py-3 align-middle text-slate-600 font-mono text-sm">{exp.invoice_number || "-"}</td>
                      <td className="px-4 py-3 align-middle text-right font-medium text-slate-800">
                        <IndianRupee className="w-3 h-3 inline mr-0.5" />{fmt(exp.amount)}
                      </td>
                      <td className="px-4 py-3 align-middle text-center">
                        <Badge className={exp.payment_status === "Paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                          {exp.payment_status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 align-middle text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEditDialog(exp)}>
                            <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDelete(exp._id)}>
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</Button>
          <span className="text-sm text-slate-500 self-center">Page {page} of {totalPages}</span>
          <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      )}

      {/* Add/Edit Expense Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Expense" : "Add Expense"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Amount *</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" />
              </div>
              <div>
                <Label className="text-xs">Date *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Vendor Name</Label>
              <Input value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} placeholder="Vendor name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Invoice Number</Label>
                <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} placeholder="INV-001" />
              </div>
              <div>
                <Label className="text-xs">Payment Status</Label>
                <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Paid">Paid</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Brief description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              {editingId ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorExpenses;
